import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, Plane, ArrowDown } from "lucide-react";

/**
 * Multi-leg "Lägg till resa"-wizard.
 *
 * Steg 1: Användaren ger resan ett namn ("Resa till JFK").
 * Steg 2: Lägg till första flyg (flight_id + datum).
 * Steg 3+: Lägg till anslutningsflyg via "+" knappen.
 *
 * Vid submit anropas POST /api/trips med hela kedjan.
 */
export function AddTripDialog({ open, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [interlineBag, setInterlineBag] = useState(true);
  const [legs, setLegs] = useState([{ flight_id: "", date: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setLabel("");
    setInterlineBag(true);
    setLegs([{ flight_id: "", date: "" }]);
  };

  const updateLeg = (idx, field, value) => {
    setLegs((arr) => arr.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };
  const addLeg = () => {
    if (legs.length >= 8) {
      toast.error("Max 8 anslutningar per resa");
      return;
    }
    setLegs((arr) => [...arr, { flight_id: "", date: "" }]);
  };
  const removeLeg = (idx) => {
    if (legs.length === 1) return;
    setLegs((arr) => arr.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    const cleaned = legs
      .map((l) => ({
        flight_id: (l.flight_id || "").trim().toUpperCase().replace(/\s+/g, ""),
        date: (l.date || "").trim() || null,
      }))
      .filter((l) => l.flight_id);
    if (!label.trim()) {
      toast.error("Ge resan ett namn (t.ex. 'Resa till JFK')");
      return;
    }
    if (cleaned.length === 0) {
      toast.error("Lägg till minst ett flyg");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/trips", {
        label: label.trim(),
        legs: cleaned,
        interline_baggage: interlineBag,
      });
      const numLegs = (data?.trip?.legs || []).length;
      const errs = data?.errors || [];
      if (errs.length > 0) {
        toast.warning(`Resa skapad med ${numLegs} flyg — ${errs.length} kunde inte läggas till`);
      } else {
        toast.success(`Resa "${label.trim()}" skapad med ${numLegs} flyg`);
      }
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skapa resan");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="add-trip-dialog"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-[color:var(--bg-surface)] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85dvh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 bg-[color:var(--bg-surface)] border-b border-[color:var(--border-subtle)] p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plane size={18} />
            Lägg till resa
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-[color:var(--bg-elevated)] rounded"
            data-testid="add-trip-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Resans namn */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1">
              Resans namn
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="t.ex. Resa till JFK"
              maxLength={80}
              data-testid="add-trip-label"
              className="w-full px-3 py-2 rounded-md bg-[color:var(--bg-elevated)] border border-transparent focus:border-[color:var(--accent-color,#6366f1)] outline-none text-sm"
            />
          </div>

          {/* Bagage */}
          <div className="card-soft p-3 flex items-start gap-3">
            <input
              type="checkbox"
              id="interline-bag"
              checked={interlineBag}
              onChange={(e) => setInterlineBag(e.target.checked)}
              className="mt-0.5"
              data-testid="add-trip-interline"
            />
            <label htmlFor="interline-bag" className="flex-1 text-sm cursor-pointer">
              <div className="font-medium">Bagaget checkas hela vägen</div>
              <div className="text-xs text-[color:var(--text-tertiary)] mt-0.5">
                Bocka av om du måste hämta och checka in bagaget vid mellanlandning.
                Edit kommer påminna dig vid varje byte.
              </div>
            </label>
          </div>

          {/* Legs */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
              Flighter i ordning
            </label>
            <div className="space-y-2">
              {legs.map((leg, idx) => (
                <div key={idx} data-testid={`add-trip-leg-${idx}`}>
                  {idx > 0 && (
                    <div className="flex items-center justify-center my-1">
                      <ArrowDown size={14} className="text-[color:var(--text-tertiary)]" />
                    </div>
                  )}
                  <div className="card-soft p-3 flex items-start gap-2">
                    <span className="text-xs font-bold mt-2 text-[color:var(--text-tertiary)] tabular-nums w-5">
                      {idx + 1}
                    </span>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={leg.flight_id}
                        onChange={(e) => updateLeg(idx, "flight_id", e.target.value)}
                        placeholder="Flightnr (SK161)"
                        maxLength={10}
                        data-testid={`add-trip-leg-flight-${idx}`}
                        className="px-2 py-1.5 rounded-md bg-[color:var(--bg-main)] border border-[color:var(--border-subtle)] focus:border-[color:var(--accent-color,#6366f1)] outline-none text-sm tabular-nums uppercase"
                      />
                      <input
                        type="date"
                        value={leg.date}
                        onChange={(e) => updateLeg(idx, "date", e.target.value)}
                        data-testid={`add-trip-leg-date-${idx}`}
                        className="px-2 py-1.5 rounded-md bg-[color:var(--bg-main)] border border-[color:var(--border-subtle)] focus:border-[color:var(--accent-color,#6366f1)] outline-none text-sm"
                      />
                    </div>
                    {legs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLeg(idx)}
                        className="p-1.5 hover:bg-[color:var(--bg-elevated)] rounded"
                        data-testid={`add-trip-leg-remove-${idx}`}
                        aria-label="Ta bort"
                      >
                        <Trash2 size={14} className="text-[color:var(--text-tertiary)]" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addLeg}
              disabled={legs.length >= 8}
              className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-md border border-dashed border-[color:var(--bg-elevated)] hover:bg-[color:var(--bg-elevated)] text-sm text-[color:var(--text-secondary)] disabled:opacity-40"
              data-testid="add-trip-add-leg"
            >
              <Plus size={14} />
              Lägg till anslutning
            </button>
            <p className="mt-2 text-[10px] text-[color:var(--text-tertiary)]">
              Datum är frivilligt — lämna tomt för &quot;idag&quot;. Om dagens flight redan
              avgått auto-rullas datum till morgondagen.
            </p>
          </div>
        </div>

        {/* Footer — alltid synlig (ej sticky, egen rad under scrollytan) */}
        <div className="shrink-0 bg-[color:var(--bg-surface)] border-t border-[color:var(--border-subtle)] p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-md border border-[color:var(--bg-elevated)] text-sm hover:bg-[color:var(--bg-elevated)]"
            data-testid="add-trip-cancel"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !label.trim() || legs.every((l) => !l.flight_id.trim())}
            className="flex-1 py-2.5 rounded-md bg-[color:var(--accent-color,#6366f1)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="add-trip-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Skapa resa
          </button>
        </div>
      </div>
    </div>
  );
}
